import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ListChecks, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { code: "", label: "" };

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-semibold text-[var(--text-1)]">
        {label}
      </Label>
      {children}
    </div>
  );
}

function CatalogFormFields({ form, setForm, idPrefix }) {
  return (
    <>
      <Field id={`${idPrefix}-code`} label="Código">
        <Input
          id={`${idPrefix}-code`}
          placeholder="Ex.: AUS"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          required
          className="font-mono"
          data-testid={`${idPrefix}-code`}
        />
      </Field>
      <Field id={`${idPrefix}-label`} label="Descrição">
        <Input
          id={`${idPrefix}-label`}
          placeholder="Ex.: Destinatário ausente"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          required
          data-testid={`${idPrefix}-label`}
        />
      </Field>
    </>
  );
}

export default function OccurrenceCatalog() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = useCallback(() => {
    api.get("/occurrences/catalog").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/occurrences/catalog", { ...form, sort_order: items.filter((i) => !i.ephemeral).length });
      toast.success("Código adicionado");
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const openEdit = (item) => {
    if (item.ephemeral) {
      toast.error("Salve este código no catálogo antes de editar (adicione um novo com o mesmo conteúdo).");
      return;
    }
    setEditItem(item);
    setEditForm({
      code: item.code || "",
      label: item.label || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editItem) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/occurrences/catalog/${editItem.id}`, editForm);
      const relinked = data?.occurrences_relinked || 0;
      toast.success(
        relinked > 0
          ? `Código atualizado · ${relinked} ocorrência(s) atualizada(s)`
          : "Código atualizado",
      );
      setEditItem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const askDelete = async (item) => {
    if (item.ephemeral) {
      toast.error("Códigos padrão temporários não podem ser excluídos. Adicione códigos próprios ao catálogo.");
      return;
    }
    setDeleteItem(item);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      const { data } = await api.delete(`/occurrences/catalog/${deleteItem.id}`);
      const unlinked = data?.occurrences_unlinked || 0;
      toast.success(
        unlinked > 0
          ? `Código excluído · ${unlinked} ocorrência(s) desvinculada(s)`
          : "Código excluído",
      );
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      throw err;
    }
  };

  const persisted = items.filter((i) => !i.ephemeral);
  const list = persisted.length ? persisted : items;

  return (
    <div className="space-y-4" data-testid="occurrence-catalog-page">
      <div>
        <h1 className="font-display font-bold text-2xl flex items-center gap-2"><ListChecks size={22} /> Catálogo de ocorrências</h1>
        <p className="text-sm text-muted-foreground">Códigos padronizados exibidos no App do Entregador</p>
      </div>

      <Card className="p-4 space-y-3">
        <div>
          <h2 className="font-semibold text-sm">Nova ocorrência</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Informe o código curto e a descrição. Essas opções aparecem no registro de ocorrência do entregador.
          </p>
        </div>
        <form onSubmit={create} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
          <CatalogFormFields form={form} setForm={setForm} idPrefix="occ-create" />
          <Button type="submit" className="h-10" data-testid="occ-create-submit">
            Adicionar
          </Button>
        </form>
      </Card>

      <div className="grid gap-2">
        {list.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            Nenhuma ocorrência no catálogo ainda.
          </Card>
        )}
        {list.map((i) => (
          <Card
            key={i.id}
            className="p-3 flex flex-wrap justify-between items-center gap-3 text-sm"
            data-testid={`occ-catalog-item-${i.id}`}
          >
            <div className="min-w-0 space-y-0.5">
              <div>
                <strong className="font-mono">{i.code}</strong>
                <span className="text-muted-foreground"> — </span>
                {i.label}
              </div>
              {i.ephemeral ? (
                <div className="text-xs text-muted-foreground">Padrão temporário (ainda não salvo)</div>
              ) : null}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEdit(i)}
                disabled={!!i.ephemeral}
                data-testid={`occ-edit-${i.id}`}
              >
                <Pencil size={14} className="mr-1" /> Editar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => askDelete(i)}
                disabled={!!i.ephemeral}
                data-testid={`occ-delete-${i.id}`}
              >
                <Trash2 size={14} className="mr-1" /> Excluir
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <ConfirmDeleteDialog
        open={!!deleteItem}
        onOpenChange={(open) => { if (!open) setDeleteItem(null); }}
        title="Excluir ocorrência do catálogo?"
        itemName={deleteItem ? `"${deleteItem.code}" — ${deleteItem.label}` : null}
        description="As ocorrências que usam esta descrição terão o motivo desvinculado."
        onConfirm={confirmDelete}
        testId="occ-confirm-delete"
      />

      <Dialog
        open={!!editItem}
        onOpenChange={(open) => { if (!open) setEditItem(null); }}
      >
        <DialogContent className="rounded-3xl max-w-md" data-testid="occ-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Editar ocorrência</DialogTitle>
            <DialogDescription>
              Ao alterar a descrição, as ocorrências já registradas com o texto anterior serão atualizadas.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <CatalogFormFields form={editForm} setForm={setEditForm} idPrefix="occ-edit" />
            <Button
              type="submit"
              disabled={saving}
              className="w-full rounded-xl h-11"
              data-testid="occ-edit-submit"
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
